import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
  Divider,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import useScreenSize from "../../../../hooks/useScreenSize";
import {
  AdvancedSearchFilter,
  AdvancedDdlResult,
  StringOperator,
  NumericOperator,
  DateOperator,
  BooleanOperator,
  ListOperator,
  AdvancedSearchSelection,
} from "../../../../services/api/types/users/users";
import { GroupedSelect } from "./GroupedSelect";
import { usePortal } from "../../../../context/PortalsContext";
import { useToast } from "../../../../context/ToastContext";
import { getAdvancedUserAttributesDDL } from "../../../../services/api/apiClient";
import { useLanguages } from "../../../../context/LanguagesContext";
import { DynamicFieldRenderer } from "../../../../components/DynamicFieldRenderer/DynamicFieldRenderer";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ReactComponent as PlusIcon } from "../../../../assets/plus.svg";
import { numberToOrdinalWord } from "../../../../utils/helpers";
import { brand } from "../../../../theme/themePrimitives";
import { useCustomisation } from "../../../../context/CustomisationContext";
import { extractErrorMessage } from "../../../../utils/errorUtils";

interface AttributesFilterProps {
  showFilter: boolean;
  setShowFilter: React.Dispatch<React.SetStateAction<boolean>>;
  selections: AdvancedSearchSelection[];
  handleChange: <K extends keyof AdvancedSearchFilter>(
    selectionIndex: number,
    filterIndex: number,
    field: K,
    value: AdvancedSearchFilter[K],
    groupHeader?: string,
    filterGroup?: string,
    initialValue?: string,
  ) => void;
  addRow: (selectionIndex: number) => void;
  removeRow: (selectionIndex: number, filterIndex: number) => void;
  getValuesForAttribute: (
    attribute: string,
    operator:
      | StringOperator
      | NumericOperator
      | DateOperator
      | BooleanOperator
      | ListOperator
      | "",
    value: string,
    selectionIndex: number,
    filterIndex: number,
  ) => void;
  handleSearch: (selectionIndex: number) => void;
  loading: boolean;
  addSelection: () => void;
  removeSelection: (selectionIndex: number) => void;
  clearAll: () => void;
}

export const AttributesFilter: React.FC<AttributesFilterProps> = ({
  setShowFilter,
  showFilter,
  selections,
  handleChange,
  addRow,
  removeRow,
  handleSearch,
  getValuesForAttribute,
  loading,
  addSelection,
  removeSelection,
  clearAll,
}) => {
  const [groupedAttributes, setGroupedAttributes] =
    useState<AdvancedDdlResult[]>();
  const [openDatepickerIndex, setOpenDatepickerIndex] = useState<string | null>(
    null,
  );
  const [openStartDatepicker, setOpenStartDatepicker] = useState(false);
  const [openEndDatepicker, setOpenEndDatepicker] = useState(false);
  const [inputValues, setInputValues] = useState<string[]>([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { isMobile } = useScreenSize();
  const { portals } = usePortal();
  const { languages } = useLanguages();
  const { showToast } = useToast();
  const { customisations } = useCustomisation();
  const {
    GPC: { buttonsSettings },
  } = customisations.customisations;
  const [hasFilterAnyValue, setHasFilterAnyValue] = useState(false);
  const primaryButtonColor = buttonsSettings?.gpcBtnPrimaryBgColor;

  useEffect(() => {
    const totalFilters = selections.reduce(
      (total, selection) => total + selection.filters.length,
      0,
    );
    if (inputValues.length !== totalFilters) {
      setInputValues(new Array(totalFilters).fill(""));
    }

    let filterIndex = 0;
    selections.forEach((selection) => {
      selection.filters.forEach((filter) => {
        if (!filter.value && inputValues[filterIndex]) {
          setInputValues((prev) => {
            const newInputValues = [...prev];
            newInputValues[filterIndex] = "";
            return newInputValues;
          });
        }
        filterIndex++;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps

    const hasAnyValue = selections.some((group) =>
      group.filters.some(
        (f) => f.attribute?.trim() || f.operator?.trim() || f.value?.trim(),
      ),
    );
    setHasFilterAnyValue(hasAnyValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections]);

  useEffect(() => {
    const fetchAttributesDDL = async (assignmentFields = true) => {
      if (showFilter && groupedAttributes === undefined) {
        try {
          setFetchLoading(true);
          if (portals.selectedPortal) {
            const response: AdvancedDdlResult[] =
              await getAdvancedUserAttributesDDL(
                portals.selectedPortal.portalUid,
                { languageCode: languages.selectedLanguage, assignmentFields },
              );
            setGroupedAttributes(response);
          }
        } catch (error: any) {
          console.log("Error: ", error);
          showToast(
            extractErrorMessage(error, "Failed to fetch attributesDDL"),
            "error",
          );
        } finally {
          setFetchLoading(false);
        }
      }
    };
    fetchAttributesDDL();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  const getEnumLabel = (key: string, dataType?: string) => {
    // Custom labels for date operators
    if (
      dataType?.toLowerCase() === "date" ||
      dataType?.toLowerCase() === "datetime"
    ) {
      const dateLabels: Record<string, string> = {
        EQUAL: "Equals",
        NOT_EQUAL: "Does not equal",
        LESS_THAN: "Is before",
        GREATER_THAN: "Is after",
        GREATER_THAN_EQUAL: "Is on or after",
        LESS_THAN_EQUAL: "Is on or before",
        BETWEEN: "Is between",
      };
      return (
        dateLabels[key] ||
        key
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/^./, (c) => c.toUpperCase())
      );
    }

    // Custom labels for string operators
    if (
      dataType?.toLowerCase() === "string" ||
      dataType?.toLowerCase() === "text"
    ) {
      const stringLabels: Record<string, string> = {
        IN: "Is in",
        NOT_IN: "Is not in",
        CONTAINS: "Contains",
        NOT_CONTAINS: "Does not contain",
        STARTS_WITH: "Starts with",
        ENDS_WITH: "Ends with",
      };
      return (
        stringLabels[key] ||
        key
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/^./, (c) => c.toUpperCase())
      );
    }

    // Default formatting for other types
    return key
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase());
  };

  const getAttributeDataType = (attributeKey: string): string | null => {
    if (!groupedAttributes) return null;
    for (const group of groupedAttributes) {
      const item = group.items.find(
        (item) => item.attributeKey === attributeKey,
      );
      if (item) {
        return item.dataType.toLowerCase();
      }
    }
    return null;
  };

  const getAttributeOperator = (attributeKey: string): any => {
    if (!groupedAttributes) return;
    for (const group of groupedAttributes) {
      const item = group.items.find(
        (item) => item.attributeKey === attributeKey,
      );
      if (item) {
        switch (item.dataType.toLowerCase()) {
          case "string":
          case "text":
            // Return only allowed string operators in exact order: Is in, Is not in, Contains, Does not contain, Starts with, Ends with
            return [
              ["IN", StringOperator.IN],
              ["NOT_IN", StringOperator.NOT_IN],
              ["CONTAINS", StringOperator.CONTAINS],
              ["NOT_CONTAINS", StringOperator.NOT_CONTAINS],
              ["STARTS_WITH", StringOperator.STARTS_WITH],
              ["ENDS_WITH", StringOperator.ENDS_WITH],
            ];
          case "number":
          case "integer":
          case "numeric":
            return NumericOperator;
          case "date":
          case "datetime":
            // Return only allowed date operators in exact order: Equals, Does not equal, Is before, Is after, Is on or after, Is on or before, Is between
            return [
              ["EQUAL", DateOperator.EQUAL],
              ["NOT_EQUAL", DateOperator.NOT_EQUAL],
              ["LESS_THAN", DateOperator.LESS_THAN],
              ["GREATER_THAN", DateOperator.GREATER_THAN],
              ["GREATER_THAN_EQUAL", DateOperator.GREATER_THAN_EQUAL],
              ["LESS_THAN_EQUAL", DateOperator.LESS_THAN_EQUAL],
              ["BETWEEN", DateOperator.BETWEEN],
            ];
          case "boolean":
            return BooleanOperator;
          case "list":
            return ListOperator;
          default:
            return [
              ["IN", StringOperator.IN],
              ["NOT_IN", StringOperator.NOT_IN],
              ["CONTAINS", StringOperator.CONTAINS],
              ["NOT_CONTAINS", StringOperator.NOT_CONTAINS],
              ["STARTS_WITH", StringOperator.STARTS_WITH],
              ["ENDS_WITH", StringOperator.ENDS_WITH],
            ];
        }
      }
    }
    return [
      ["IN", StringOperator.IN],
      ["NOT_IN", StringOperator.NOT_IN],
      ["CONTAINS", StringOperator.CONTAINS],
      ["NOT_CONTAINS", StringOperator.NOT_CONTAINS],
      ["STARTS_WITH", StringOperator.STARTS_WITH],
      ["ENDS_WITH", StringOperator.ENDS_WITH],
    ];
  };

  const getAttributeName = (attributeKey: string): string => {
    if (!groupedAttributes) return attributeKey;
    for (const group of groupedAttributes) {
      const item = group.items.find(
        (item) => item.attributeKey === attributeKey,
      );
      if (item) {
        return item.attributeName || attributeKey;
      }
    }
    return attributeKey;
  };

  const getAttributeOptions = (
    attributeKey: string,
  ): { key: string; value: string }[] => {
    if (!groupedAttributes) return [];
    for (const group of groupedAttributes) {
      const item = group.items.find(
        (item) => item.attributeKey === attributeKey,
      );
      if (item && Array.isArray((item as any).result)) {
        return (item as any).result as Array<{ key: string; value: string }>;
      }
    }
    return [];
  };

  const getChipsFromValue = (value: string): string[] => {
    if (!value) return [];
    return value
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v);
  };

  const getGlobalFilterIndex = (
    selectionIndex: number,
    filterIndex: number,
  ): number => {
    let globalIndex = 0;
    for (let i = 0; i < selectionIndex; i++) {
      globalIndex += selections[i].filters.length;
    }
    return globalIndex + filterIndex;
  };

  const handleAddChip = (
    selectionIndex: number,
    filterIndex: number,
    inputValue: string,
  ) => {
    if (!inputValue.trim()) return;

    const currentChips = getChipsFromValue(
      selections[selectionIndex].filters[filterIndex].value,
    );
    const newChips = [...currentChips, inputValue.trim()];
    const newValue = newChips.join(", ");

    handleChange(selectionIndex, filterIndex, "value", newValue);

    const globalIndex = getGlobalFilterIndex(selectionIndex, filterIndex);
    const newInputValues = [...inputValues];
    newInputValues[globalIndex] = "";
    setInputValues(newInputValues);
  };

  const handleClose = () => {
    const totalFilters = selections.reduce(
      (total, selection) => total + selection.filters.length,
      0,
    );
    setInputValues(new Array(totalFilters).fill(""));
    setFieldErrors({});
    setShowFilter(false);
  };

  const validateField = (
    fieldType: "attribute" | "operator" | "value",
    value: string,
  ): string => {
    if (!value.trim()) {
      return `${
        fieldType.charAt(0).toUpperCase() + fieldType.slice(1)
      } is required`;
    }
    return "";
  };

  const clearFieldError = (fieldKey: string) => {
    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldKey];
      return newErrors;
    });
  };

  const handleSearchClick = () => {
    setFieldErrors({});

    let globalIndex = 0;
    let hasErrors = false;
    const newErrors: Record<string, string> = {};

    selections.forEach((selection, selectionIndex) => {
      selection.filters.forEach((filter, filterIndex) => {
        const attributeKey = `${selectionIndex}-${filterIndex}-attribute`;
        const attributeError = validateField("attribute", filter.attribute);
        if (attributeError) {
          newErrors[attributeKey] = attributeError;
          hasErrors = true;
        }

        const operatorKey = `${selectionIndex}-${filterIndex}-operator`;
        const operatorError = validateField("operator", filter.operator);
        if (operatorError) {
          newErrors[operatorKey] = operatorError;
          hasErrors = true;
        }

        const valueKey = `${selectionIndex}-${filterIndex}-value`;
        const dataType = getAttributeDataType(filter.attribute);
        const effectiveValue =
          dataType === "boolean" && !filter.value ? "false" : filter.value;
        const valueError = validateField("value", effectiveValue);
        if (valueError) {
          newErrors[valueKey] = valueError;
          hasErrors = true;
        }

        globalIndex++;
      });
    });

    if (hasErrors) {
      setFieldErrors(newErrors);
      return;
    }

    globalIndex = 0;
    selections.forEach((selection, selectionIndex) => {
      selection.filters.forEach((_, filterIndex) => {
        if (inputValues[globalIndex]?.trim()) {
          handleAddChip(selectionIndex, filterIndex, inputValues[globalIndex]);
        }
        globalIndex++;
      });
    });

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setTimeout(() => {
      handleSearch(0);
      const totalFilters = selections.reduce(
        (total, selection) => total + selection.filters.length,
        0,
      );
      setInputValues(new Array(totalFilters).fill(""));
    }, 0);
  };

  return (
    <Drawer
      anchor="right"
      open={showFilter}
      onClose={handleClose}
      disableEnforceFocus
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "white",
            boxSizing: "border-box",
            width: isMobile ? "100%" : "450px",
            overflowX: "hidden",
          },
        },
      }}
    >
      {fetchLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            height: "100%",
            alignItems: "center",
          }}
        >
          <CircularProgress size={100} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", overflow: "hidden" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "1rem",
            }}
          >
            <Typography variant="h6">Advanced Filters</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflowY: "auto", p: "1rem" }}>
            {groupedAttributes && groupedAttributes?.length > 0 && (
              <Stack spacing={2}>
                {selections.map((selection, selectionIndex) => (
                  <React.Fragment key={selectionIndex}>
                    <Stack spacing={2} direction={"column"}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "0 1rem",
                        }}
                      >
                        <Typography variant="h6">
                          Selection {selectionIndex + 1}
                        </Typography>
                        {selections.length > 1 && (
                          <IconButton
                            onClick={() => removeSelection(selectionIndex)}
                          >
                            <CloseIcon />
                          </IconButton>
                        )}
                      </Box>
                      <Stack spacing={2}>
                        {selection.filters.map((filter, filterIndex) => (
                          <Stack
                            spacing={2}
                            sx={{
                              border: `1px solid #D0D5DD`,
                              borderRadius: "12px",
                            }}
                            key={filterIndex}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                p: "1rem 1rem 0 1rem",
                              }}
                            >
                              <Typography variant="h6">
                                {numberToOrdinalWord(filterIndex + 1)} filter
                              </Typography>
                              {selection.filters.length > 1 && (
                                <IconButton
                                  onClick={() =>
                                    removeRow(selectionIndex, filterIndex)
                                  }
                                >
                                  <CloseIcon />
                                </IconButton>
                              )}
                            </Box>
                            <Divider />
                            <Stack spacing={2} p={"0 1rem 1rem 1rem"}>
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)",
                                  gap: 1,
                                }}
                              >
                                <Stack spacing={1} sx={{ minWidth: 0, overflow: "hidden" }}>
                                  <Typography variant="subtitle2">
                                    Attribute
                                  </Typography>
                                  <GroupedSelect
                                    value={filter.attribute}
                                    onChange={(
                                      value,
                                      groupHeader,
                                      filterGroup,
                                    ) => {
                                      handleChange(
                                        selectionIndex,
                                        filterIndex,
                                        "attribute",
                                        value,
                                        groupHeader,
                                        filterGroup,
                                        getAttributeDataType(value) === "boolean" ? "false" : undefined,
                                      );
                                      clearFieldError(
                                        `${selectionIndex}-${filterIndex}-attribute`,
                                      );
                                    }}
                                    data={groupedAttributes}
                                  />
                                  {fieldErrors[
                                    `${selectionIndex}-${filterIndex}-attribute`
                                  ] && (
                                    <Typography
                                      color="error"
                                      variant="caption"
                                      sx={{ mt: 0.5 }}
                                    >
                                      {
                                        fieldErrors[
                                          `${selectionIndex}-${filterIndex}-attribute`
                                        ]
                                      }
                                    </Typography>
                                  )}
                                </Stack>
                                <Stack spacing={1} sx={{ minWidth: 0, overflow: "hidden" }}>
                                  <Typography variant="subtitle2">
                                    Operator
                                  </Typography>
                                  <Select
                                    disabled={filter.attribute === ""}
                                    value={filter.operator}
                                    onChange={(e) => {
                                      handleChange(
                                        selectionIndex,
                                        filterIndex,
                                        "operator",
                                        e.target.value,
                                      );
                                      clearFieldError(
                                        `${selectionIndex}-${filterIndex}-operator`,
                                      );
                                    }}
                                    IconComponent={ExpandMoreIcon}
                                    sx={{
                                      borderRadius: "12px",
                                      width: "100%",
                                      backgroundColor: "transparent",
                                      "&:hover": {
                                        backgroundColor: "transparent",
                                      },
                                      "& .MuiSelect-select": {
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "8.5px 14px",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      },
                                    }}
                                    size="medium"
                                  >
                                    {filter.attribute &&
                                      getAttributeOperator(filter.attribute) &&
                                      (Array.isArray(
                                        getAttributeOperator(filter.attribute),
                                      )
                                        ? getAttributeOperator(
                                            filter.attribute,
                                          ).map(
                                            ([key, value]: [
                                              string,
                                              string,
                                            ]) => (
                                              <MenuItem key={key} value={value}>
                                                {getEnumLabel(
                                                  key,
                                                  getAttributeDataType(
                                                    filter.attribute,
                                                  ) || undefined,
                                                )}
                                              </MenuItem>
                                            ),
                                          )
                                        : Object.entries(
                                            getAttributeOperator(
                                              filter.attribute,
                                            ),
                                          ).map(([key, value]) => (
                                            <MenuItem
                                              key={key}
                                              value={value as string}
                                            >
                                              {getEnumLabel(
                                                key,
                                                getAttributeDataType(
                                                  filter.attribute,
                                                ) || undefined,
                                              )}
                                            </MenuItem>
                                          )))}
                                  </Select>
                                  {fieldErrors[
                                    `${selectionIndex}-${filterIndex}-operator`
                                  ] && (
                                    <Typography
                                      color="error"
                                      variant="caption"
                                      sx={{ mt: 0.5 }}
                                    >
                                      {
                                        fieldErrors[
                                          `${selectionIndex}-${filterIndex}-operator`
                                        ]
                                      }
                                    </Typography>
                                  )}
                                </Stack>
                              </Box>
                              <Stack spacing={1} width={"100%"}>
                                <Typography variant="subtitle2">
                                  Value
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                  }}
                                >
                                  <DynamicFieldRenderer
                                    key={`${selectionIndex}-${filterIndex}-${filter.attribute}-${getAttributeDataType(
                                      filter.attribute,
                                    )}`}
                                    config={{
                                      dataType:
                                        getAttributeDataType(
                                          filter.attribute,
                                        ) || "string",
                                      attributeKey: filter.attribute,
                                      attributeName: getAttributeName(
                                        filter.attribute,
                                      ),
                                    }}
                                    value={filter.value}
                                    options={getAttributeOptions(
                                      filter.attribute,
                                    )}
                                    onChange={(newValue) => {
                                      handleChange(
                                        selectionIndex,
                                        filterIndex,
                                        "value",
                                        String(newValue || ""),
                                      );
                                      clearFieldError(
                                        `${selectionIndex}-${filterIndex}-value`,
                                      );
                                    }}
                                    mode="filter"
                                    operator={filter.operator as any}
                                    openDatepicker={
                                      openDatepickerIndex ===
                                      `${selectionIndex}-${filterIndex}`
                                    }
                                    onOpenDatepicker={() =>
                                      setOpenDatepickerIndex(
                                        `${selectionIndex}-${filterIndex}`,
                                      )
                                    }
                                    onCloseDatepicker={() =>
                                      setOpenDatepickerIndex(null)
                                    }
                                    openStartDatepicker={openStartDatepicker}
                                    onOpenStartDatepicker={() =>
                                      setOpenStartDatepicker(true)
                                    }
                                    onCloseStartDatepicker={() =>
                                      setOpenStartDatepicker(false)
                                    }
                                    openEndDatepicker={openEndDatepicker}
                                    onOpenEndDatepicker={() =>
                                      setOpenEndDatepicker(true)
                                    }
                                    onCloseEndDatepicker={() =>
                                      setOpenEndDatepicker(false)
                                    }
                                  />
                                  {getAttributeDataType(filter.attribute) ===
                                    "string" && (
                                    <IconButton
                                      onClick={() => {
                                        const attributeKey = `${selectionIndex}-${filterIndex}-attribute`;
                                        const attributeError = validateField(
                                          "attribute",
                                          filter.attribute,
                                        );
                                        if (attributeError) {
                                          setFieldErrors({
                                            [attributeKey]: attributeError,
                                          });
                                          return;
                                        }
                                        getValuesForAttribute(
                                          filter.attribute,
                                          filter.operator,
                                          filter.value,
                                          selectionIndex,
                                          filterIndex,
                                        );
                                      }}
                                      disabled={!filter.operator}
                                    >
                                      <VisibilityIcon />
                                    </IconButton>
                                  )}
                                </Box>
                                {fieldErrors[
                                  `${selectionIndex}-${filterIndex}-value`
                                ] && (
                                  <Typography
                                    color="error"
                                    variant="caption"
                                    sx={{ mt: 0.5 }}
                                  >
                                    {
                                      fieldErrors[
                                        `${selectionIndex}-${filterIndex}-value`
                                      ]
                                    }
                                  </Typography>
                                )}
                              </Stack>
                              {filterIndex === selection.filters.length - 1 && (
                                <>
                                  <Divider />
                                  <Button
                                    onClick={() => addRow(selectionIndex)}
                                    startIcon={
                                      <PlusIcon style={{ color: brand[600] }} />
                                    }
                                    disabled={
                                      getAttributeDataType(filter.attribute) ===
                                      "boolean"
                                    }
                                    sx={{
                                      px: 2,
                                      borderRadius: "12px",
                                      border: `1px solid ${brand[600]}`,
                                      backgroundColor: `white`,
                                      color: brand[600],
                                    }}
                                  >
                                    <Typography fontWeight={"bold"}>
                                      Add filter
                                    </Typography>
                                  </Button>
                                </>
                              )}
                            </Stack>
                          </Stack>
                        ))}
                      </Stack>
                    </Stack>
                    <Box sx={{ width: "100%" }}>
                      <Divider textAlign="center">
                        <Typography>OR</Typography>
                      </Divider>
                    </Box>
                  </React.Fragment>
                ))}
                <Button
                  onClick={addSelection}
                  startIcon={<PlusIcon style={{ color: "black" }} />}
                  sx={{
                    px: 2,
                    borderRadius: "12px",
                    border: `1px solid black`,
                    backgroundColor: `white`,
                    color: `black`,
                  }}
                >
                  <Typography fontWeight={"bold"}>Add selection</Typography>
                </Button>
              </Stack>
            )}
          </Box>
          <Divider />
          <Box
            sx={{
              p: "1rem",
              display: "flex",
              gap: "1rem",
            }}
          >
            {loading ? (
              <CircularProgress />
            ) : (
              <Button
                onClick={handleSearchClick}
                sx={{
                  px: 2,
                  borderRadius: "12px",
                  backgroundColor: `${brand[600]} !important`,
                  color: `white`,
                  "&:hover": {
                    bgcolor: `${brand[700]} !important`,
                    fontWeight: 700,
                  },
                }}
              >
                <Typography>Apply filters</Typography>
              </Button>
            )}
            <Button
              onClick={() => {
                clearAll();
                setFieldErrors({});
                setShowFilter(true);
              }}
              sx={{
                px: 2,
                borderRadius: "12px",
                backgroundColor: `#fff`,
                border: `1px solid ${primaryButtonColor}`,
                color: `${primaryButtonColor}`,
                fontWeight: 600,
                // Fix for border showing on disabled
                "&.Mui-disabled": {
                  border: "1px solid #4d4d4d42",
                  color: "#999",
                  backgroundColor: "#fff",
                },
              }}
              disabled={!hasFilterAnyValue}
            >
              <Typography>Clear all</Typography>
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};
